import {
    AccountSwitcherMenu as BaseAccountSwitcherMenu,
    type SwitcherAccount,
} from '@ionq/legacy-ui';
import type React from 'react';
import { useMemo } from 'react';
import { useSession } from './SessionProvider';

// Orgs-only adapter over the shared switcher (enterprise's flavor leads with
// the hub). A pick reports through onSelect — the session's
// switchOrganization POSTs the same-origin switch-account and refetches.
type OrganizationSwitcherMenuProps = Pick<
    React.ComponentProps<typeof BaseAccountSwitcherMenu>,
    'trigger' | 'positioning'
>;

/** Whether any user|owner membership beyond the current org exists — the
 * console's only switch operation. AppSidebar gates the context row on it
 * (single membership: full wordmark, no row). */
export function useHasSwitchTarget(): boolean {
    const { organizations, currentOrganization } = useSession();
    return organizations.some(
        (org) =>
            (org.userRole === 'user' || org.userRole === 'owner') &&
            org.id !== currentOrganization.id,
    );
}

export function OrganizationSwitcherMenu({
    trigger,
    positioning,
}: OrganizationSwitcherMenuProps) {
    const { organizations, currentOrganization, switchOrganization } =
        useSession();

    // The current org stays in the list, check-marked (selectedId). Only
    // org accounts (user|owner) are offered — enforced here
    // on top of the server's users.me filter: a partner-wide enterprise
    // identity is not a membership the console can switch into.
    const switcherOrganizations = useMemo<SwitcherAccount[]>(() => {
        const items: SwitcherAccount[] = organizations
            .filter(
                (org) => org.userRole === 'user' || org.userRole === 'owner',
            )
            .map(({ id, name, logoUrl }) => ({
                id,
                name,
                logoUrl,
            }))
            // The session list carries no order of its own — by label, so
            // the menu reads the same on every open.
            .sort((a, b) => (a.name || a.id).localeCompare(b.name || b.id));
        // The session org can be missing from the membership list (deleted
        // under the session) — keep the selected row renderable.
        if (!items.some((org) => org.id === currentOrganization.id)) {
            items.unshift({
                id: currentOrganization.id,
                name: currentOrganization.name,
                logoUrl: currentOrganization.logoUrl,
            });
        }
        return items;
    }, [organizations, currentOrganization]);

    // No membership beyond the current org = nothing to switch to: no menu
    // (AppSidebar and AppNavbar omit the context row on the same
    // condition — full wordmark in the band, a bare toggle in the bar).
    const hasSwitchTarget = useHasSwitchTarget();
    if (!hasSwitchTarget) return null;

    return (
        <BaseAccountSwitcherMenu
            sections={[
                {
                    label: 'Switch organization',
                    accounts: switcherOrganizations,
                    search: 'Search organizations...',
                    emptyLabel: 'No organizations found',
                },
            ]}
            selectedId={currentOrganization.id}
            onSelect={(account) => switchOrganization(account.id)}
            trigger={trigger}
            positioning={positioning}
        />
    );
}
